import { useEffect, useRef, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useLanguage } from '../../core/hooks/use-language';
import { useTranslation } from '../../core/hooks/use-translation';
import { BellIcon, CalendarIcon, CaseIcon, ChatIcon, UserIcon } from '../../ui/portal-icons';

/**
 * The portal's sections, one URL each.
 *
 * **Links rather than a `tablist`.** Each section is a route, so the back button, a bookmark and a
 * link in an email all land on the right one — and a set of links is announced as navigation, which
 * is what it is. `NavLink` sets `aria-current` on the active one.
 *
 * On a telephone the row scrolls sideways rather than wrapping: five labels in two ragged lines
 * read as two menus. The active section is scrolled into the row's view, or on a telephone the
 * notifications and profile tabs would be selected and off-screen.
 */
export function PortalTabs({ unreadNotifications }: { unreadNotifications: number }) {
    const { t } = useTranslation();
    const language = useLanguage();
    const base = `/${language}/portal`;
    const { pathname } = useLocation();
    const row = useRef<HTMLElement>(null);

    // scrollLeft rather than scrollIntoView, which would also scroll the page to the tab bar.
    useEffect(() => {
        const nav = row.current;
        const active = nav === null ? null : nav.querySelector<HTMLElement>('[aria-current="page"]');

        if (nav === null || active === null) return;

        const start = active.offsetLeft - nav.offsetLeft;
        const end = start + active.offsetWidth;

        if (start < nav.scrollLeft || end > nav.scrollLeft + nav.clientWidth) {
            nav.scrollLeft = start - (nav.clientWidth - active.offsetWidth) / 2;
        }
    }, [pathname]);

    return (
        <nav
            ref={row}
            aria-label={t('portal.sections')}
            className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0"
        >
            <ul className="flex w-max min-w-full gap-1 rounded-2xl border border-slate-200/80 bg-white p-1 shadow-sm shadow-slate-900/[0.03] dark:border-slate-800 dark:bg-slate-900 dark:shadow-none">
                <Tab to={base} end icon={<CaseIcon className="size-4" />} label={t('portal.cases')} />
                <Tab
                    to={`${base}/appointments`}
                    icon={<CalendarIcon className="size-4" />}
                    label={t('portal.appointments')}
                />
                <Tab to={`${base}/messages`} icon={<ChatIcon className="size-4" />} label={t('portal.messages')} />
                <Tab
                    to={`${base}/notifications`}
                    icon={<BellIcon className="size-4" />}
                    label={t('portal.notifications')}
                    count={unreadNotifications}
                />
                <Tab to={`${base}/profile`} icon={<UserIcon className="size-4" />} label={t('portal.profile')} />
            </ul>
        </nav>
    );
}

function Tab({
    to,
    end = false,
    icon,
    label,
    count = 0
}: {
    to: string;
    end?: boolean;
    icon: ReactNode;
    label: string;
    count?: number;
}) {
    return (
        <li className="flex-1">
            <NavLink
                to={to}
                end={end}
                className={({ isActive }) =>
                    `flex items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-sm font-semibold whitespace-nowrap transition sm:gap-2 sm:px-3.5 ${
                        isActive
                            ? 'bg-brand-600 text-white shadow-sm'
                            : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200'
                    }`
                }
            >
                {({ isActive }) => (
                    <>
                        {icon}
                        {label}
                        {count > 0 && (
                            <span
                                className={`rounded-full px-1.5 py-0.5 text-xs leading-none font-bold tabular-nums ${
                                    isActive ? 'bg-white/20 text-white' : 'bg-brand-600 text-white'
                                }`}
                            >
                                {count}
                            </span>
                        )}
                    </>
                )}
            </NavLink>
        </li>
    );
}
